import { memo, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { OdinButton } from '@/components/ui/odin/odin-button'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { OdinStatusBadge } from '@/components/ui/odin/odin-status-badge'
import { messagesSelectors, setAgentActionStatus, setChatStatus } from 'store/messages'
import { confirmAgentAction } from 'api/agents'
import { showErrorToast } from 'utils/toast'
import { AgentAction } from 'types/agent'
import { AgentActionStatusEnum } from 'types/chat-message'
import { analytics, ANALYTICS_EVENTS } from 'services/analytics'

type AgentActionCardProps = {
  action: AgentAction
  /** True once the bot streamed a continuation after the action — i.e. it resolved. */
  resolved: boolean
}

const formatValue = (value: unknown): string => {
  if (value == null) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * Inline approval card for a paused agent write action (HITL). Shows a readable
 * recap of the action (its `summary`, falling back to params when absent) and
 * lets the user approve or reject — no HTTP verbs, endpoints or raw ids. On
 * decision it relays to the backend; the bot's continuation streams onto the
 * same message. Once concluded it collapses to a title + accepted/rejected badge,
 * expandable for the recap.
 */
const AgentActionCardComponent = ({ action, resolved }: AgentActionCardProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const chatId = useSelector(messagesSelectors.chatId)
  const serverStatus = useSelector(messagesSelectors.agentActionStatusByRun(action.runId))
  const [submitting, setSubmitting] = useState<AgentActionStatusEnum | null>(null)
  // Live messages carry no agentRunId yet, so the server status only lands after a
  // reload — remember our own decision so the badge shows immediately.
  const [decided, setDecided] = useState<AgentActionStatusEnum | null>(null)

  const status = serverStatus ?? decided
  const accepted = status === AgentActionStatusEnum.ACCEPTED
  const rejected = status === AgentActionStatusEnum.REJECTED
  const settled = accepted || rejected

  // Once the action is concluded (bot continued or decided), collapse it.
  const concluded = resolved || settled
  const [expanded, setExpanded] = useState(false)
  const showDetails = !concluded || expanded

  const params = useMemo(() => Object.entries(action.params ?? {}), [action.params])
  // Pending, the tool name goes next to the ask: the summary describes the change, not which
  // action is running, and once decided the card collapses to the name alone.
  const title = concluded ? action?.tool : [t('agentActionTitle'), action?.tool].filter(Boolean).join(' · ')

  // The resumed run answers over the websocket like any other question, and can take a while:
  // put the chat in the same waiting state a sent question does, or the user decides and then
  // stares at nothing until the first token lands. The stream handlers clear it (streaming ->
  // idle), so nothing else has to.
  //
  // It starts when the decision has settled, NOT on click: until then the buttons are still
  // there with their own spinner, and two loaders for one wait read as something being stuck.
  useEffect(() => {
    if (!chatId || !submitting || !settled) return

    dispatch(setChatStatus({ chatId, status: 'awaitingBot' }))
  }, [chatId, submitting, settled, dispatch])

  const decide = async (approved: boolean) => {
    if (!chatId || settled || submitting) return
    const status = approved ? AgentActionStatusEnum.ACCEPTED : AgentActionStatusEnum.REJECTED
    setSubmitting(status)
    try {
      await confirmAgentAction({ chatId, runId: action.runId, approved })
      analytics.track(ANALYTICS_EVENTS.AGENT_ACTION_DECIDED, {
        decision: approved ? 'accepted' : 'rejected',
        tool: action.tool,
      })
      setDecided(status)
      dispatch(setAgentActionStatus({ runId: action.runId, status }))
      // The confirm answers only once the resumed run is over, so the write has happened by now:
      // tell the host page what changed, so it can refresh the entity the agent just edited.
      if (approved) {
        setTimeout(() => {
          window.parent.postMessage(
            {
              type: 'agentActionCompleted',
              payload: {
                runId: action.runId,
                tool: action.tool,
                method: action.method,
                endpoint: action.endpoint,
                params: action.params,
              },
            },
            '*',
          )
        }, 1000)
      }
    } catch (e) {
      console.error('[agentic] confirm failed', e)
      showErrorToast(t('agentActionError'))
      setSubmitting(null)
      dispatch(setChatStatus({ chatId, status: 'idle' }))
    }
  }

  return (
    <div className="my-1 flex flex-col gap-2">
      <div
        className={clsx('flex items-center gap-2', concluded && 'cursor-pointer select-none')}
        role={concluded ? 'button' : undefined}
        aria-expanded={concluded ? expanded : undefined}
        onClick={concluded ? () => setExpanded((value) => !value) : undefined}
      >
        <OdinStatusBadge circle variant="info" icon={['fas', 'wand-magic-sparkles']} />
        <span className="min-w-0 truncate text-sm font-semibold text-foreground">{title}</span>
        {settled && (
          <OdinStatusBadge
            className="ml-auto"
            variant={accepted ? 'positive' : 'negative'}
            icon={['far', accepted ? 'check-circle' : 'times-circle']}
            label={t(accepted ? 'agentActionApproved' : 'agentActionRejected')}
          />
        )}
        {concluded && (
          <OdinIcon
            icon={['far', 'chevron-down']}
            className={clsx(
              'h-3 w-3 shrink-0 transition-transform duration-300 motion-reduce:transition-none',
              !settled && 'ml-auto',
              expanded && 'rotate-180',
              'text-muted-foreground',
            )}
          />
        )}
      </div>

      <div
        className="grid transition-all duration-300 ease-out motion-reduce:transition-none"
        style={{ gridTemplateRows: showDetails ? '1fr' : '0fr' }}
      >
        <div className="flex min-h-0 flex-col gap-2 overflow-hidden">
          {action.summary ? (
            <p className="m-0 text-sm leading-snug text-muted-foreground">{action.summary}</p>
          ) : (
            params.length > 0 && (
              <dl className="m-0 flex flex-col gap-2">
                {params.map(([key, value]) => (
                  <div key={key} className="flex min-w-0 flex-col gap-0.5 text-xs leading-snug">
                    <dt className="font-semibold wrap-break-word text-muted-foreground">{key}</dt>
                    <dd className="m-0 wrap-break-word text-foreground">{formatValue(value)}</dd>
                  </div>
                ))}
              </dl>
            )
          )}

          {!resolved && !settled && (
            <div className="flex gap-1 pt-1">
              <OdinButton
                size="sm"
                disabled={!!submitting}
                loading={submitting === AgentActionStatusEnum.ACCEPTED}
                onClick={() => decide(true)}
              >
                {t('agentActionApprove')}
              </OdinButton>
              <OdinButton
                size="sm"
                variant="secondary"
                disabled={!!submitting}
                loading={submitting === AgentActionStatusEnum.REJECTED}
                onClick={() => decide(false)}
              >
                {t('agentActionReject')}
              </OdinButton>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export const AgentActionCard = memo(AgentActionCardComponent)
